import type { TouristRoom, TouristsSelection } from '../model/types';

export const MIN_ADULTS = 1;
export const MAX_ADULTS = 6;
export const MAX_CHILDREN_PER_ROOM = 3;
export const MIN_CHILD_AGE = 0;
export const MAX_CHILD_AGE = 17;
export const MIN_ROOMS = 1;
export const MAX_ROOMS = 8;
export const NEW_ROOM_ADULTS = 2;

export const DEFAULT_ROOM: TouristRoom = { adults: NEW_ROOM_ADULTS, childrenAges: [] };
export const DEFAULT_TOURISTS: TouristsSelection = {
  rooms: [{ adults: NEW_ROOM_ADULTS, childrenAges: [] }],
};

export function clampAdults(n: number): number {
  const rounded = Math.round(Number(n));
  if (Number.isNaN(rounded)) {
    return MIN_ADULTS;
  }
  return Math.min(MAX_ADULTS, Math.max(MIN_ADULTS, rounded));
}

export function clampChildAge(n: number): number {
  const rounded = Math.round(Number(n));
  if (Number.isNaN(rounded)) {
    return MIN_CHILD_AGE;
  }
  return Math.min(MAX_CHILD_AGE, Math.max(MIN_CHILD_AGE, rounded));
}

export function normalizeRoom(room: Partial<TouristRoom> | null | undefined): TouristRoom {
  if (!room) {
    return { ...DEFAULT_ROOM };
  }
  const adults = clampAdults(room.adults ?? NEW_ROOM_ADULTS);
  const ages = Array.isArray(room.childrenAges) ? room.childrenAges : [];
  const childrenAges = ages.slice(0, MAX_CHILDREN_PER_ROOM).map(clampChildAge);
  return { adults, childrenAges };
}

export function normalizeTourists(sel: TouristsSelection | null | undefined): TouristsSelection {
  if (!sel || !Array.isArray(sel.rooms) || sel.rooms.length === 0) {
    return { rooms: DEFAULT_TOURISTS.rooms.map((room) => ({ ...room, childrenAges: [...room.childrenAges] })) };
  }
  return { rooms: sel.rooms.slice(0, MAX_ROOMS).map(normalizeRoom) };
}

export function setRoomAdults(
  sel: TouristsSelection,
  roomIndex: number,
  value: number,
): TouristsSelection {
  const { rooms } = normalizeTourists(sel);
  if (roomIndex < 0 || roomIndex >= rooms.length) {
    return { rooms };
  }
  return {
    rooms: rooms.map((room, i) =>
      i === roomIndex ? { ...room, adults: clampAdults(value) } : room,
    ),
  };
}

export function addRoom(sel: TouristsSelection): TouristsSelection {
  const { rooms } = normalizeTourists(sel);
  if (rooms.length >= MAX_ROOMS) {
    return { rooms };
  }
  return { rooms: [...rooms, { ...DEFAULT_ROOM, childrenAges: [] }] };
}

export function removeRoom(sel: TouristsSelection, roomIndex: number): TouristsSelection {
  const { rooms } = normalizeTourists(sel);
  if (rooms.length <= MIN_ROOMS || roomIndex < 0 || roomIndex >= rooms.length) {
    return { rooms };
  }
  return { rooms: rooms.filter((_, i) => i !== roomIndex) };
}

export function addChild(sel: TouristsSelection, roomIndex: number, age: number): TouristsSelection {
  const { rooms } = normalizeTourists(sel);
  if (roomIndex < 0 || roomIndex >= rooms.length) {
    return { rooms };
  }
  return {
    rooms: rooms.map((room, i) => {
      if (i !== roomIndex || room.childrenAges.length >= MAX_CHILDREN_PER_ROOM) {
        return room;
      }
      return { ...room, childrenAges: [...room.childrenAges, clampChildAge(age)] };
    }),
  };
}

export function setChildAge(
  sel: TouristsSelection,
  roomIndex: number,
  childIndex: number,
  age: number,
): TouristsSelection {
  const { rooms } = normalizeTourists(sel);
  if (roomIndex < 0 || roomIndex >= rooms.length) {
    return { rooms };
  }
  return {
    rooms: rooms.map((room, i) => {
      if (i !== roomIndex || childIndex < 0 || childIndex >= room.childrenAges.length) {
        return room;
      }
      return {
        ...room,
        childrenAges: room.childrenAges.map((a, j) => (j === childIndex ? clampChildAge(age) : a)),
      };
    }),
  };
}

export function removeChild(
  sel: TouristsSelection,
  roomIndex: number,
  childIndex: number,
): TouristsSelection {
  const { rooms } = normalizeTourists(sel);
  if (roomIndex < 0 || roomIndex >= rooms.length) {
    return { rooms };
  }
  return {
    rooms: rooms.map((room, i) => {
      if (i !== roomIndex || childIndex < 0 || childIndex >= room.childrenAges.length) {
        return room;
      }
      return { ...room, childrenAges: room.childrenAges.filter((_, j) => j !== childIndex) };
    }),
  };
}

export function totalTourists(sel: TouristsSelection): number {
  return sel.rooms.reduce((sum, room) => sum + room.adults + room.childrenAges.length, 0);
}

export function isValidTourists(sel: TouristsSelection): boolean {
  if (!sel || !Array.isArray(sel.rooms)) {
    return false;
  }
  const { rooms } = sel;
  if (rooms.length < MIN_ROOMS || rooms.length > MAX_ROOMS) {
    return false;
  }
  return rooms.every((room) => {
    if (
      !Number.isInteger(room.adults) ||
      room.adults < MIN_ADULTS ||
      room.adults > MAX_ADULTS
    ) {
      return false;
    }
    if (!Array.isArray(room.childrenAges) || room.childrenAges.length > MAX_CHILDREN_PER_ROOM) {
      return false;
    }
    return room.childrenAges.every(
      (age) => Number.isInteger(age) && age >= MIN_CHILD_AGE && age <= MAX_CHILD_AGE,
    );
  });
}
